






























































































































































import Vue, { VueConstructor } from 'vue';
import { UserMixin } from '@/mixins/UserMixin';
import { CreateWatchListForUserRequest, EditFormField, Indexable, UpdateWatchListRequest, WatchList } from '@/models';
import { RouteName } from '@/router/RouteName';
import { loadingService, loggerService, watchListService } from '@/services';
import { Utilities } from '@/utilities';
import { Subscription } from 'rxjs';

export default (Vue as VueConstructor<Vue & InstanceType<typeof UserMixin>>).extend({
  name: 'WatchLists',

  mixins: [UserMixin],

  data: () => ({
    pageLoading: false,
    loadingSubscription: new Subscription(),
    watchLists: [] as WatchList[],
    showDeleteDialog: false,
    stagedWatchListToDelete: null as WatchList | null,
    watchListCommon: {
      nameRules: [(name: string) => !!name || 'Name is required']
    },
    createNewWatchList: {
      loading: false,
      formValid: false,
      showDialog: false,
      watchListToCreate: {} as CreateWatchListForUserRequest
    },
    editWatchList: {
      stagedWatchList: null as null | WatchList,
      watchListToEdit: {
        name: { edited: false, value: '' },
        description: { edited: false, value: '' }
      } as { [key in keyof Required<UpdateWatchListRequest>]: EditFormField<string> },
      loading: false,
      formValid: false,
      showDialog: false,
      watchListIndex: NaN
    }
  }),

  methods: {
    goToWatchList(watchListId: number): void {
      this.$router.push({ name: RouteName.WatchList, params: { id: watchListId.toString() } });
    },

    cancelDeleteStagedWatchList(): void {
      this.showDeleteDialog = false;
      this.stagedWatchListToDelete = null;
    },

    async deleteStagedWatchList(): Promise<void> {
      if (!this.stagedWatchListToDelete) {
        return;
      }

      try {
        await watchListService.deleteWatchListForUser(this.userId, this.stagedWatchListToDelete.id);
        this.watchLists = this.watchLists.filter(list => list.id !== this.stagedWatchListToDelete?.id);
      } catch (error) {
        console.error(error);
      } finally {
        this.stagedWatchListToDelete = null;
        this.showDeleteDialog = false;
      }
    },

    async createWatchList(): Promise<void> {
      const { watchListToCreate } = this.createNewWatchList;

      if (Utilities.isEmptyObject(watchListToCreate)) {
        loggerService.warn(`watchListToCreate is empty ${watchListToCreate}`);
        return;
      }

      try {
        this.createNewWatchList.loading = true;
        const newWatchList = await watchListService.createWatchListForUser(this.userId, watchListToCreate);
        this.watchLists.push(newWatchList);
      } catch (error) {
        console.error(error);
      } finally {
        this.createNewWatchList.loading = false;
        this.createNewWatchList.showDialog = false;
        this.resetCreateWatchListForm();
      }
    },

    stageEditWatchList(watchList: WatchList): void {
      this.editWatchList.watchListIndex = this.watchLists.indexOf(watchList);

      Object.keys(this.editWatchList.watchListToEdit).forEach(key => {
        this.editWatchList.watchListToEdit[key] = {
          edited: false,
          value: (watchList as Indexable)[key]
        };
      });
      this.editWatchList.stagedWatchList = watchList;
      this.editWatchList.showDialog = true;
    },

    async saveEditWatchList(): Promise<void> {
      const { watchLists } = this;
      const { stagedWatchList, watchListToEdit, watchListIndex } = this.editWatchList;
      if (!watchListToEdit || !stagedWatchList) {
        // this.errorMessage = 'No budget staged for editing.';
        loggerService.error('No watch list staged for editing.');
        this.closeEditWatchListDialog();
        return;
      }

      if (Utilities.isEmptyObject(this.editedWatchListValues)) {
        this.closeEditWatchListDialog();
        return;
      }

      this.editWatchList.loading = true;

      try {
        const updatedWatchList = await watchListService.updateWatchListForUser(
          this.userId,
          stagedWatchList.id,
          this.editedWatchListValues
        );
        watchLists.splice(watchListIndex, 1, { ...stagedWatchList, ...updatedWatchList });
      } catch (error) {
        // this.errorMessage = `Unable to update budget: ${error.message}`;
        loggerService.error(`Unable to update watch list: ${(error as any).message}`);
      } finally {
        this.closeEditWatchListDialog();
        this.editWatchList.loading = false;
      }
    },

    closeEditWatchListDialog(): void {
      this.editWatchList.stagedWatchList = null;
      this.editWatchList.watchListIndex = NaN;
      this.editWatchList.showDialog = false;
      Object.keys(this.editWatchList.watchListToEdit).forEach(key => {
        this.editWatchList.watchListToEdit[key] = {
          edited: false,
          value: ''
        };
      });
      this.resetEditWatchListForm();
    },

    resetCreateWatchListForm(): void {
      (this.$refs.createWatchListForm as any).reset();
    },

    resetEditWatchListForm(): void {
      (this.$refs.editWatchListForm as any).reset();
    }
  },

  computed: {
    editedWatchListValues(): UpdateWatchListRequest {
      return Object.entries(this.editWatchList.watchListToEdit)
        .filter(([, value]) => value && value.edited)
        .reduce((prev, [key, { value }]) => ({ ...prev, [key]: value }), {});
    }
  },

  async mounted(): Promise<void> {
    this.loadingSubscription = loadingService.loadingStateChanged.subscribe(loading => (this.pageLoading = loading));
    loadingService.startLoading();

    this.watchLists = (await watchListService.getWatchListsForUser(this.userId)).toArray();

    loadingService.stopLoading();
  },

  beforeDestroy(): void {
    this.loadingSubscription.unsubscribe();
  }
});
